import React, {
    useEffect,
    useReducer,
    useCallback
} from 'react';

import PropTypes from 'prop-types';

import {
    WizardSectionPanel
} from './WizardSectionPanel.jsx';

import {
    useCurationConfigState
} from '../../../../hooks/widgetConfiguration.js';

import {
    RIQInfoPopover
} from '../../../../components/RIQPopover.jsx';

import {
    listeningDefinitions
} from '../enums/listeningDefinitions.jsx';

import useGetSources from '../../hooks/useGetSources.js';
import useHasCompanionMode from '../../hooks/useHasCompanionMode.js';

const {
    chooseSourceTitle,
    chooseSourceDescription,
    availableInQuid
} = listeningDefinitions['saved-search'];


function SourceSelect({sourceKey, sourceName, isChecked, isDisabled, onSourceChange}) {

    function onClick(e) {
        e.stopPropagation();

        if (isDisabled) {
            return;
        }
        onSourceChange(sourceKey);
    }

    return (
        <button
            key={sourceKey}
            className='tlw-m-0 tlw-mb-2.5 tlw-flex tlw-items-center tlw-border-0 tlw-bg-transparent tlw-p-0 '
            onClick={onClick}
        >
            <div
                className='tlw-h-10 tlw-w-10'
            >
                <input
                    type='checkbox'
                    checked={isChecked}
                    disabled={isDisabled}
                    readOnly
                />
            </div>

            <span className='tlw-ml-5'>
                {sourceName}
            </span>
        </button>
    );
}

ChooseSource.propTypes = {
    setIsSourceInvalid: PropTypes.func.isRequired
};

function createSelectedSourceIds(allSources, source) {
    if (source === null || source === undefined) {
        return allSources;
    }

    if (_.isString(source)) {
        return _.keyBy(source.split(','));
    }

    return _.keyBy(source);
}


export default function ChooseSource({setIsSourceInvalid}) {

    const [source, setSource] = useCurationConfigState('source');
    const applicableSources = useGetSources();
    const hasCompanionMode = useHasCompanionMode();

    const sourceReducer = useCallback((state, action) => {
        switch (action.type) {
            case 'toggle-all':
                return (_.size(applicableSources) !== _.size(state) ? createSelectedSourceIds(applicableSources, null) : {});

            case 'update':
                return createSelectedSourceIds(applicableSources, action.source);

            default:
                throw new Error('invalid reducer dispatch received');
        }
    }, [applicableSources]);

    const [selectedSourceIds, dispatch] = useReducer(
        sourceReducer,
        source,
        (initialSource) => createSelectedSourceIds(applicableSources, initialSource)
    );

    // NOTE in this case we have state that depends on state, and worse still,
    // a Reducer is supposed to be pure, so in the place where we know about
    // the status of validation we cannot call `setIsSourceInvalid`. so,
    // instead, since we know that `selectedSourceIds` will always reflect the
    // correct set of selection, we `useEffect` here to update the parent about
    // validation state.
    useEffect(() => {
        setIsSourceInvalid(_.isEmpty(selectedSourceIds));
    }, [setIsSourceInvalid, selectedSourceIds]);

    const onSourceChange = function (sourceKey) {
        if (sourceKey === 'all') {
            if (source) {
                setSource(null);
            }

            dispatch({
                type: 'toggle-all'
            });
        } else {
            let newSourceIds;
            if (selectedSourceIds[sourceKey]) {
                newSourceIds = _.without(_.keys(selectedSourceIds), sourceKey);
            } else {
                newSourceIds = _.concat(_.keys(selectedSourceIds), [sourceKey]);
            }

            setSource(newSourceIds);

            dispatch({
                type: 'update',
                source: newSourceIds
            });
        }
    };

    return (
        <WizardSectionPanel
            title={chooseSourceTitle}
            description={chooseSourceDescription(hasCompanionMode)}
            collapsable={true}
        >
            <div className='tlw-mx-8 tlw-flex tlw-flex-col tlw-py-5 tlw-text-gray-900'>

                {_.isEmpty(selectedSourceIds) &&
                    <div
                        className='tlw-mx-2 tlw-mb-5 tlw-text-red-500'
                    >
                        At least one source must be selected
                    </div>
                }

                <SourceSelect
                    key='all'
                    sourceKey='all'
                    sourceName='All'
                    isChecked={_.keys(selectedSourceIds).length === _.keys(applicableSources).length}
                    onSourceChange={onSourceChange}
                />
                {_.map(applicableSources, (sourceDetails, sourceKey) => {
                    return (
                        <SourceSelect
                            key={sourceKey}
                            sourceKey={sourceKey}
                            sourceName={sourceDetails.name}
                            isChecked={Boolean(selectedSourceIds[sourceKey])}
                            onSourceChange={onSourceChange}
                        />
                    );
                })}

                <div
                    className='tlw-mt-4 tlw-text-sm'
                >
                    <span className='fa fa-facebook' />&nbsp;
                    <span className='fa fa-instagram' />&nbsp;
                    <span className='fa fab fa-tiktok' />&nbsp;

                    available in Quid
                    <RIQInfoPopover
                        description={availableInQuid}
                    />
                </div>
            </div>

        </WizardSectionPanel>
    );
}
